import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card, Pagination, ToastContext, useFetch } from '@reveni-io/reveni-ui'

import FilterViews from '@/common/components/FilterViews/FilterViews'
import useViewActions from '@/common/components/FilterViews/hooks/useViewActions/useViewActions'
import useViews from '@/common/components/FilterViews/hooks/useViews/useViews'
import PageSizeSelector from '@/common/components/PageSizeSelector/PageSizeSelector'
import AppContext from '@/common/context/app.context'
import useCarriers from '@/common/hooks/useCarriers/useCarriers'
import useFilters from '@/common/hooks/useFilters/useFilters'
import useLocations from '@/common/hooks/useLocations/useLocations'
import useRegions from '@/common/hooks/useRegions/useRegions'
import commonServices from '@/common/services/services'
import usePrepareParcelItems from '@/parcels/hooks/usePrepareParcelItems/usePrepareParcelItems'
import parcelServices from '@/parcels/services/services'
import { transformResponseParcels } from '@/parcels/transformers/parcels/transformers'

import ExportModal from './ExportModal/ExportModal'
import Filters from './Filters/Filters'
import GeneratePackingListModal from './GeneratePackingListModal/GeneratePackingListModal'
import GeneratePickingListModal from './GeneratePickingListModal/GeneratePickingListModal'
import useExportParcels from './hooks/useExportParcels/useExportParcels'
import useGeneratePackingList from './hooks/useGeneratePackingList/useGeneratePackingList'
import useGeneratePickingList from './hooks/useGeneratePickingList/useGeneratePickingList'
import useOpenParcelDetail from './hooks/useOpenParcelDetail/useOpenParcelDetail'
import useParcelsPresentationType from './hooks/useParcelsPresentationType/useParcelsPresentationType'
import usePrepareParcels from './hooks/usePrepareParcels/usePrepareParcels'
import useRequestPickupDrawer from './hooks/useRequestPickupDrawer/useRequestPickupDrawer'
import useSelectedParcels from './hooks/useSelectedParcels/useSelectedParcels'
import ParcelDetailDrawer from './ParcelDetailDrawer/ParcelDetailDrawer'
import ParcelsListContent from './ParcelsListContent/ParcelsListContent'
import PrepareParcelsErrorModal from './PrepareParcelsErrorModal/PrepareParcelsErrorModal'
import PrepareParcelsModal from './PrepareParcelsModal/PrepareParcelsModal'
import RequestPickupDrawer from './RequestPickupDrawer/RequestPickupDrawer'
import { filterNames, parcelDirectionConfig } from './constants'
import { getParcelRequestParams } from './helpers'
import * as Styles from './styles'

const ParcelsListPage = ({ direction }) => {
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigation = useNavigate()
  const { filtersContextKey, viewType, showBulkActions, filters, filterTypes, sections, defaultViews } =
    parcelDirectionConfig[direction]

  const {
    data: parcels,
    loading,
    fetchData: getParcels,
  } = useFetch(parcelServices.getParcels, {
    doInitialRequest: false,
    transformResponse: transformResponseParcels,
  })

  const {
    views,
    selectedView,
    setSelectedView,
    duplicateView,
    deleteView,
    hasViewChanges,
    setHasViewChanges,
    saveView,
    createView,
  } = useViews({
    storeId: info?.selectedStore?.id,
    defaultViews,
    type: viewType,
  })

  const {
    query,
    orderBy,
    parcelType,
    updateFilter,
    refetch,
    creationDateStart,
    creationDateEnd,
    shipmentCreationDaysGte,
    shipmentCreationDaysLte,
    shipmentStatus,
    shipmentNormalizedTrackingStatus,
    shipmentOrigin,
    shipmentDestination,
    onChangeView,
    shipmentException,
    shipmentsPageSize,
    shipmentsCarrier,
    shipmentsCarrierService,
    shipmentExternalCreatedGte,
    shipmentExternalCreatedLte,
    shipmentExternalCreatedDaysGte,
    shipmentExternalCreatedDaysLte,
    shipmentPackedAtGte,
    shipmentPackedAtLte,
    shipmentPackedAtDaysGte,
    shipmentPackedAtDaysLte,
    shipmentReadyDateGte,
    shipmentReadyDateLte,
    shipmentReadyDateDaysGte,
    shipmentReadyDateDaysLte,
    shipmentShippedAtGte,
    shipmentShippedAtLte,
    shipmentShippedAtDaysGte,
    shipmentShippedAtDaysLte,
    shipmentDeliveredAtGte,
    shipmentDeliveredAtLte,
    shipmentDeliveredAtDaysGte,
    shipmentDeliveredAtDaysLte,
    shipmentLocation,
    claimStatus,
    claimType,
    claimCreatedAtGte,
    claimCreatedAtLte,
    claimCreatedAtDaysGte,
    claimCreatedAtDaysLte,
    getFilters,
  } = useFilters({
    filters,
    doInitialRequest: true,
    fetchData: params => {
      return getParcels({
        storeId: info.selectedStore.id,
        params: getParcelRequestParams(params, direction),
      })
    },
    filterValues: info?.[filtersContextKey] || selectedView?.filters,
    nameOfFiltersInContext: filtersContextKey,
    prevCursor: parcels?.previous,
    nextCursor: parcels?.next,
  })

  const { handleChangeView, handleSaveView, handleCreateView, handleDiscardViewChanges } = useViewActions({
    setSelectedView,
    onChangeView,
    saveView,
    createView,
    getFilters,
    setHasViewChanges,
    selectedView,
  })

  const { regions, loading: fetchingRegions } = useRegions()
  const { locations, fetchingLocations } = useLocations({ storeId: info?.selectedStore?.id })
  const { carriers } = useCarriers({ storeId: info?.selectedStore?.id, doInitialRequest: true })
  const { data: carrierServices } = useFetch(
    () => commonServices.getCarrierServices({ storeId: info?.selectedStore?.id, section: 'shipments' }),
    { doInitialRequest: true }
  )

  const { parcelPresentationType, parcelPresentationOptions } = useParcelsPresentationType()
  const { selectedParcels, setSelectedParcels, selectParcel, selectAll } = useSelectedParcels({
    parcels: parcels?.results,
  })

  const {
    showPrepareParcelsModal,
    openPrepareParcelsModal,
    closePrepareParcelsModal,
    onPrepareParcels,
    showPrepareParcelsErrorModal,
    closePrepareParcelsErrorModal,
    failedParcels,
    preparingParcels,
    preparableParcels,
  } = usePrepareParcels({
    storeId: info?.selectedStore?.id,
    setToastInfo,
    refetch,
    setSelectedParcels,
    selectedParcels,
  })

  const {
    showGeneratePackingListModal,
    openGeneratePackingListModal,
    closeGeneratePackingListModal,
    onGeneratePackingList,
    generatingPackingList,
    packableParcels,
  } = useGeneratePackingList({
    storeId: info?.selectedStore?.id,
    setToastInfo,
    setSelectedParcels,
    selectedParcels,
  })

  const {
    showGeneratePickingListModal,
    openGeneratePickingListModal,
    closeGeneratePickingListModal,
    onGeneratePickingList: onGeneratePickingListAction,
    generatingPickingList,
    pickableParcels,
  } = useGeneratePickingList({
    storeId: info?.selectedStore?.id,
    setToastInfo,
    setSelectedParcels,
    selectedParcels,
  })

  const getExportFilters = () => getParcelRequestParams(getFilters(), direction)

  const { showExportModal, openExportModal, closeExportModal, onExportResults, exportingResults } = useExportParcels({
    getFilters: getExportFilters,
    setToastInfo,
    storeId: info?.selectedStore?.id,
  })

  const {
    showRequestPickupDrawer,
    openRequestPickupDrawer,
    closeRequestPickupDrawer,
    loadingCarriersToPickup,
    carriersToPickup,
    selectedCarriers,
    handleCheckCarrier,
    totalShipmentsCount,
    schedulePickups,
    loadingSchedulePickups,
    selectedLocationId,
    handleChangeLocation,
  } = useRequestPickupDrawer({
    storeId: info?.selectedStore?.id,
    carriers,
    setToastInfo,
    refetch,
  })

  const {
    showParcelDetail,
    onCloseParcelDetail,
    onNavigateToParcelDetail,
    getParcelDetailHref,
    onParcelDetailClick,
    parcelDetailSelectedId,
    parcelDetail,
    fetchingParcel,
    fetchParcel,
    onNextParcel,
    onPrevParcel,
  } = useOpenParcelDetail({
    storeId: info?.selectedStore?.id,
    parcels: parcels?.results,
    openParcelDetailDrawerWithId: info?.openParcelDetailDrawerWithId,
    updateContext,
  })

  const { onPrepareParcel, onAutoPrepareParcel, preparingParcel, allowPrepareParcel } = usePrepareParcelItems({
    items: parcelDetail?.related?.line_items,
    storeId: info?.selectedStore?.id,
    parcelId: parcelDetail?.id,
    relatedId: parcelDetail?.related?.id,
    navigation,
    setToastInfo,
    fetchParcel,
    updateContext,
    parcel: parcelDetail,
  })

  useEffect(() => {
    if (parcels) {
      updateContext(context => ({ ...context, parcels }))
    }
  }, [parcels, updateContext])

  const handleExpandParcelDetail = () => onNavigateToParcelDetail(parcelDetailSelectedId)

  const onChangeFilter = (name, data, doRequest) => {
    if (![filterNames.orderBy, filterNames.shipmentsCursor].includes(name)) {
      updateFilter(filterNames.shipmentsCursor, '', false)
    }

    if (![filterNames.query, filterNames.shipmentsCursor].includes(name)) setHasViewChanges(true)

    updateFilter(name, data, doRequest)
  }

  const handleChangePageSize = pageSize => onChangeFilter(filterNames.pageSize, pageSize)
  const handleChangePage = (page, cursor) => onChangeFilter(filterNames.shipmentsCursor, cursor)

  return (
    <>
      <FilterViews
        customViews={views}
        selectedView={selectedView}
        onChangeView={handleChangeView}
        defaultViews={defaultViews}
        onDuplicateView={duplicateView}
        onDeleteView={deleteView}
        hasViewChanges={hasViewChanges}
        onSaveChanges={handleSaveView}
        onCreateView={handleCreateView}
        onDiscardChanges={handleDiscardViewChanges}
        onExportResults={openExportModal}
      />
      <Card showBorder={false}>
        <Styles.CardContainer>
          <Filters
            onChangeFilter={onChangeFilter}
            query={query}
            orderBy={orderBy}
            onApplyFilters={refetch}
            direction={direction}
            filterTypes={filterTypes}
            sections={sections}
            filters={{
              parcelType,
              shipmentStatus,
              shipmentNormalizedTrackingStatus,
              shipmentOrigin,
              shipmentDestination,
              shipmentLocation,
              creationDates: { startDate: creationDateStart, endDate: creationDateEnd },
              shipmentException,
              shipmentCreationDaysGte,
              shipmentCreationDaysLte,
              shipmentsCarrier,
              shipmentsCarrierService,
              shipmentExternalCreatedDates: {
                startDate: shipmentExternalCreatedGte,
                endDate: shipmentExternalCreatedLte,
              },
              shipmentExternalCreatedDaysGte,
              shipmentExternalCreatedDaysLte,
              shipmentPackedAtDates: { startDate: shipmentPackedAtGte, endDate: shipmentPackedAtLte },
              shipmentPackedAtDaysGte,
              shipmentPackedAtDaysLte,
              shipmentReadyDate: {
                startDate: shipmentReadyDateGte,
                endDate: shipmentReadyDateLte,
              },
              shipmentReadyDateDaysGte,
              shipmentReadyDateDaysLte,
              shipmentShippedAtDates: { startDate: shipmentShippedAtGte, endDate: shipmentShippedAtLte },
              shipmentShippedAtDaysGte,
              shipmentShippedAtDaysLte,
              shipmentDeliveredAtDates: { startDate: shipmentDeliveredAtGte, endDate: shipmentDeliveredAtLte },
              shipmentDeliveredAtDaysGte,
              shipmentDeliveredAtDaysLte,
              claimStatus,
              claimType,
              claimCreatedAtDates: { startDate: claimCreatedAtGte, endDate: claimCreatedAtLte },
              claimCreatedAtDaysGte,
              claimCreatedAtDaysLte,
            }}
            regions={regions}
            carriers={carriers}
            carrierServices={carrierServices || []}
            locations={locations}
            onOpenRequestPickupDrawer={openRequestPickupDrawer}
          />
          <ParcelsListContent
            loading={loading || fetchingRegions || fetchingLocations}
            parcels={parcels}
            parcelPresentationType={parcelPresentationType}
            parcelPresentationOptions={parcelPresentationOptions}
            showBulkActions={showBulkActions}
            selectedParcels={selectedParcels}
            onSelectAll={selectAll}
            onPrepare={openPrepareParcelsModal}
            onGeneratePackingList={openGeneratePackingListModal}
            onGeneratePickingList={openGeneratePickingListModal}
            preparableParcels={preparableParcels}
            packableParcels={packableParcels}
            pickableParcels={pickableParcels}
            onSelectParcel={selectParcel}
            getParcelDetailHref={getParcelDetailHref}
            onParcelDetailClick={onParcelDetailClick}
            parcelDetailSelectedId={parcelDetailSelectedId}
          />
          <Styles.PaginationContainer>
            <PageSizeSelector value={shipmentsPageSize} onChange={handleChangePageSize} />
            <Pagination
              next={parcels?.next}
              previous={parcels?.previous}
              onChangePage={handleChangePage}
              isCursorPagination
            />
          </Styles.PaginationContainer>
        </Styles.CardContainer>
      </Card>
      <ParcelDetailDrawer
        show={showParcelDetail}
        onClose={onCloseParcelDetail}
        parcelDetail={parcelDetail}
        onViewParcelDetail={handleExpandParcelDetail}
        loading={fetchingParcel}
        onPrevParcel={onPrevParcel}
        onNextParcel={onNextParcel}
        shippingSkipPacking={info?.selectedStore?.preferences?.shipping_skip_packing}
        onPrepareParcel={onPrepareParcel}
        onAutoPrepareParcel={onAutoPrepareParcel}
        preparingParcel={preparingParcel}
        allowPrepareParcel={allowPrepareParcel}
      />
      <PrepareParcelsModal
        show={showPrepareParcelsModal}
        onClose={closePrepareParcelsModal}
        onPrepareParcels={onPrepareParcels}
        selectedParcels={preparableParcels}
        loading={preparingParcels}
      />
      <PrepareParcelsErrorModal
        show={showPrepareParcelsErrorModal}
        onClose={closePrepareParcelsErrorModal}
        failedParcels={failedParcels}
      />
      <GeneratePackingListModal
        show={showGeneratePackingListModal}
        onClose={closeGeneratePackingListModal}
        onGeneratePackingList={onGeneratePackingList}
        selectedParcels={packableParcels}
        loading={generatingPackingList}
      />
      <GeneratePickingListModal
        show={showGeneratePickingListModal}
        onClose={closeGeneratePickingListModal}
        onGeneratePickingList={onGeneratePickingListAction}
        selectedParcels={pickableParcels}
        loading={generatingPickingList}
      />
      <ExportModal
        show={showExportModal}
        onClose={closeExportModal}
        onExport={onExportResults}
        exporting={exportingResults}
      />
      <RequestPickupDrawer
        show={showRequestPickupDrawer}
        onClose={closeRequestPickupDrawer}
        loading={loadingCarriersToPickup}
        carriersToPickup={carriersToPickup}
        selectedCarriers={selectedCarriers}
        handleCheckCarrier={handleCheckCarrier}
        totalShipmentsCount={totalShipmentsCount}
        onSchedulePickups={schedulePickups}
        loadingSchedulePickups={loadingSchedulePickups}
        selectedLocationId={selectedLocationId}
        onChangeLocation={handleChangeLocation}
      />
    </>
  )
}

export default ParcelsListPage
